'use client';
import { useState, useEffect } from 'react';
import { use } from 'react';
import { getVideoById, getVideos } from '@/lib/api';
import { Video } from '@/lib/types';
import VideoCard from '@/components/ui/VideoCard';
import SectionHeader from '@/components/ui/SectionHeader';
import Link from 'next/link';

export default function VideoDetailPage({ params }: { params: Promise<{ id: string }> }) {
  const { id } = use(params);
  const [video, setVideo] = useState<Video | null>(null);
  const [relatedVideos, setRelatedVideos] = useState<Video[]>([]);
  const [loading, setLoading] = useState(true);
  const [isPlaying, setIsPlaying] = useState(false);

  useEffect(() => {
    async function loadData() {
      setLoading(true);
      try {
        const data = await getVideoById(id);
        setVideo(data);
        
        const related = await getVideos(data?.category);
        setRelatedVideos(related.filter(v => String(v.id) !== String(id)).slice(0, 4));
      } catch (error) {
        console.error('Error loading video detail:', error);
      } finally {
        setLoading(false);
      }
    }
    loadData();
    setIsPlaying(false); // Reset when id changes
  }, [id]);

  if (loading) {
    return <div className="max-w-[1400px] mx-auto px-5 py-20 text-center text-white bg-[#0D0D0D] min-h-[60vh]">Loading Video...</div>;
  }

  if (!video) {
    return <div className="max-w-[1400px] mx-auto px-5 py-20 text-center">Video not found.</div>;
  }

  const isClassName = video.imageBg?.startsWith('bg-');
  const imageStyle = !isClassName ? { backgroundImage: `url(${video.imageBg})` } : {};
  const imageClass = `${isClassName ? video.imageBg : 'bg-gray-100'} bg-cover bg-center bg-no-repeat`;

  return (
    <div className="max-w-[1400px] mx-auto px-5 py-8">
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-8">
        {/* PLAYER AREA */}
        <div>
          <div className="bg-black aspect-video w-full relative mb-6 group overflow-hidden">
            {isPlaying ? (
              <video 
                src={video.videoUrl} 
                controls 
                autoPlay 
                className="w-full h-full object-contain"
              />
            ) : (
              <>
                <div 
                  className={`absolute inset-0 ${imageClass} opacity-60`}
                  style={imageStyle}
                />
                <div className="absolute inset-0 flex items-center justify-center">
                   <button 
                    onClick={() => setIsPlaying(true)}
                    className="w-20 h-20 circle-avatar border-4 border-white bg-black/40 flex items-center justify-center hover:bg-[#C8102E] hover:border-[#C8102E] transition-all cursor-pointer group/play"
                   >
                      <svg viewBox="0 0 24 24" fill="white" className="w-8 h-8 ml-1 group-hover/play:scale-110 transition-transform">
                        <polygon points="5,3 19,12 5,21" />
                      </svg>
                   </button>
                </div>
              </>
            )}
          </div>

          <div className="border-b border-[#D4D4D4] pb-6 mb-6">
            <span className="font-['IBM_Plex_Sans',system-ui,sans-serif] font-bold text-[12px] tracking-[0.1em] uppercase text-[#C8102E] mb-2 block">
              {video.category}
            </span>
            <h1 className="font-['Playfair_Display',Georgia,serif] font-bold text-[32px] leading-[1.2] text-[#0D0D0D] mb-4">
              {video.title}
            </h1>
            <div className="flex items-center gap-6 text-[#6B6B6B] font-['IBM_Plex_Sans',system-ui,sans-serif] text-[13px]">
              <span>{video.duration}</span>
              <span>Published recently</span>
              <button className="flex items-center gap-1.5 hover:text-[#0D0D0D]">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" /></svg>
                Share
              </button>
            </div>
          </div>

          <div className="prose prose-sm max-w-none font-['Source_Serif_4',Georgia,serif] text-[17px] leading-[1.6] text-[#2D2D2D]">
            <p>
              In this exclusive report from Veritas News, we dive deep into {video.title.toLowerCase()}. 
              Our correspondents on the ground provide unprecedented access and analysis of the evolving situation.
            </p>
            <p>
              Stay tuned for more updates as this story develops. Watch the full report above.
            </p>
          </div>
        </div>

        {/* SIDEBAR — UP NEXT */}
        <div className="lg:border-l lg:border-[#D4D4D4] lg:pl-8">
          <SectionHeader title="Up Next" />
          <div className="space-y-6">
            {relatedVideos.map(v => (
              <VideoCard key={v.id} video={v} />
            ))}
          </div>
          
          <div className="mt-10 bg-[#F5F4F0] p-6 border-t-[3px] border-[#C8102E]">
            <h3 className="font-['Playfair_Display',Georgia,serif] font-bold text-[18px] mb-2">Weekly Video Briefing</h3>
            <p className="font-['IBM_Plex_Sans',system-ui,sans-serif] text-[13px] text-[#6B6B6B] mb-4">The most important visual stories, delivered to your inbox.</p>
            <button className="w-full bg-[#0D0D0D] text-white py-2.5 font-['IBM_Plex_Sans',system-ui,sans-serif] font-semibold text-[13px] hover:bg-[#333] transition-colors">
              Subscribe
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
